// frontend/src/components/NewCourseForm.js
import React, { useState, useContext } from 'react';
import { AuthContext } from './AuthContext'; // Importar AuthContext
import api from '../api';

const NewCourseForm = ({ onCancel, onSave }) => {
  const { currentUser } = useContext(AuthContext);
  const [courseData, setCourseData] = useState({
    title: '',
    description: '',
    category: '',
    level: 'beginner',
    language: 'spanish',
    price: 0,
    thumbnail: '', // Cambiado a string para URL
    requirements: [''],
    whatYouWillLearn: [''],
    sections: [], // Inicializar secciones vacías
    resources: [], // Inicializar recursos vacíos
    announcements: [], // Inicializar anuncios vacíos
    qa: [] // Inicializar QA vacío
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCourseData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...courseData[field]];
    newArray[index] = value;
    setCourseData(prev => ({ ...prev, [field]: newArray }));
  };

  const addArrayItem = (field) => {
    setCourseData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayItem = (field, index) => {
    const newArray = courseData[field].filter((_, i) => i !== index);
    setCourseData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert('Debes ser un instructor para crear un curso.');
      return;
    }

    try {
      const payload = {
        title: courseData.title,
        description: courseData.description,
        category: courseData.category,
        level: courseData.level,
        language: courseData.language,
        price: Number(courseData.price) || 0,
        goals: courseData.whatYouWillLearn.filter(Boolean),
        requirements: courseData.requirements.filter(Boolean),
        sections: []
      };

      const response = await api.post('/courses', payload);
      const newCourse = response.data?.data;

      if (!newCourse) {
        throw new Error('No se recibió el curso creado desde el servidor');
      }

      onSave(newCourse);
    } catch (error) {
      console.error('Error al crear curso:', error);
      alert(error.response?.data?.message || 'No se pudo crear el curso en la base de datos.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">Crear Nuevo Curso</h2>

      <form onSubmit={handleSubmit}>
        {/* Sección de información básica */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Información básica</h3>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">Título del curso*</label>
              <input
                type="text"
                name="title"
                value={courseData.title}
                onChange={handleChange}
                placeholder="Ej: React Avanzado - De cero a experto"
                className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Descripción*</label>
              <textarea
                name="description"
                value={courseData.description}
                onChange={handleChange}
                rows="4"
                placeholder="Describe qué aprenderán tus estudiantes en este curso..."
                className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 mb-2">Categoría*</label>
                <select
                  name="category"
                  value={courseData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                  required
                >
                  <option value="">Selecciona una categoría</option>
                  <option value="development">Desarrollo</option>
                  <option value="business">Negocios</option>
                  <option value="design">Diseño</option>
                  <option value="marketing">Marketing</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 mb-2">Nivel*</label>
                <select
                  name="level"
                  value={courseData.level}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                  required
                >
                  <option value="beginner">Principiante</option>
                  <option value="intermediate">Intermedio</option>
                  <option value="advanced">Avanzado</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 mb-2">URL de la imagen del curso*</label>
              <input
                type="url"
                name="thumbnail"
                value={courseData.thumbnail}
                onChange={handleChange}
                placeholder="Ej: https://via.placeholder.com/300x200"
                className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                required
              />
            </div>
          </div>
        </div>

        {/* Sección de requisitos */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Requisitos</h3>
          {courseData.requirements.map((req, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                value={req}
                onChange={(e) => handleArrayChange('requirements', index, e.target.value)}
                className="flex-1 px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                required
              />
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeArrayItem('requirements', index)}
                  className="ml-2 text-red-500 hover:text-red-700"
                >
                  Eliminar
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem('requirements')}
            className="mt-2 text-purple-600 hover:text-purple-800"
          >
            + Agregar otro requisito
          </button>
        </div>

        {/* Sección de lo que aprenderán */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">¿Qué aprenderán los estudiantes?</h3>
          {courseData.whatYouWillLearn.map((item, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                value={item}
                onChange={(e) => handleArrayChange('whatYouWillLearn', index, e.target.value)}
                className="flex-1 px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                required
              />
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeArrayItem('whatYouWillLearn', index)}
                  className="ml-2 text-red-500 hover:text-red-700"
                >
                  Eliminar
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem('whatYouWillLearn')}
            className="mt-2 text-purple-600 hover:text-purple-800"
          >
            + Agregar otro punto de aprendizaje
          </button>
        </div>

        {/* Sección de precio */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Precio</h3>
          <div className="flex items-center">
            <span className="mr-2">$</span>
            <input
              type="number"
              name="price"
              value={courseData.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-32 px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
              required
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            Guardar Curso
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewCourseForm;
